<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PriorityIcon from 'dashboard/routes/dashboard/inbox/components/PriorityIcon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  agentInfo: {
    type: Object,
    default: null,
  },
  loadingAgent: {
    type: Boolean,
    default: false,
  },
  conversationData: {
    type: Object,
    default: null,
  },
  loadingConversation: {
    type: Boolean,
    default: false,
  },
  currentStageName: {
    type: String,
    default: '',
  },
  itemDescription: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['navigate-to-conversation', 'context-menu']);

const { t } = useI18n();

// Recreate computed properties needed locally or pass them as props if complex
const priorityInfo = computed(() => {
  const priority = props.item.item_details?.priority?.toLowerCase() || 'none';
  if (priority === 'none') return null;

  const priorityMap = {
    high: {
      label: t('KANBAN.PRIORITY_LABELS.HIGH'),
      class: 'bg-ruby-50 dark:bg-ruby-800 text-ruby-800 dark:text-ruby-50',
    },
    medium: {
      label: t('KANBAN.PRIORITY_LABELS.MEDIUM'),
      class:
        'bg-yellow-50 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-50',
    },
    low: {
      label: t('KANBAN.PRIORITY_LABELS.LOW'),
      class: 'bg-green-50 dark:bg-green-800 text-green-800 dark:text-green-50',
    },
  };
  return priorityMap[priority] || null;
});

const priorityClass = computed(() => {
  const priorityMap = {
    high: 'bg-ruby-50 dark:bg-ruby-800 text-ruby-800 dark:text-ruby-50',
    medium:
      'bg-yellow-50 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-50',
    low: 'bg-green-50 dark:bg-green-800 text-green-800 dark:text-green-50',
    none: 'bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-50',
  };
  return (
    priorityMap[props.item.item_details?.priority?.toLowerCase() || 'none'] ||
    priorityMap.none
  );
});

const formattedValue = computed(() => {
  if (!props.item.item_details?.value) return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(props.item.item_details.value);
});

const truncatedSenderName = computed(() => {
  const name = props.conversationData?.contact?.name;
  const email = props.conversationData?.contact?.email;
  const defaultText = t('KANBAN.ITEM_CONVERSATION.NO_CONTACT');
  const text = name || email || defaultText;

  return text.length > 10 ? `${text.substring(0, 10)}...` : text;
});

const formatStageDate = item => {
  if (!item || !item.stage_entered_at) return '';
  try {
    return t('KANBAN.STAGE.TIME_IN_STAGE', {
      time: formatDistanceToNow(new Date(item.stage_entered_at), {
        locale: ptBR,
        addSuffix: false,
      }),
    });
  } catch (error) {
    // console.error('Erro ao calcular tempo na etapa:', error);
    return '';
  }
};

const onNavigateToConversation = (e, conversationDisplayId) => {
  emit('navigate-to-conversation', e, conversationDisplayId);
};

const onContextMenu = (e, conversationId) => {
  emit('context-menu', e, conversationId);
};

// Novo computed para extrair o progresso nas etapas
const stageProgress = computed(() => {
  // Busca as etapas no local esperado ou alternativo
  const stages =
    props.item.item_details?.funnel?.stages ||
    props.item.funnel?.stages ||
    null;
  const activities = props.item.item_details?.activities || [];

  if (!stages || !activities.length) {
    return { stages: [], currentStageIndex: -1, completedStages: [] };
  }

  // Converte stages object para array ordenado por position
  const stagesArray = Object.entries(stages)
    .map(([key, stage]) => ({
      key,
      name: stage.name,
      color: stage.color,
      position: stage.position,
    }))
    .sort((a, b) => a.position - b.position);

  // Extrai as mudanças de etapa das atividades
  const stageChanges = activities
    .filter(activity => activity.type === 'stage_changed')
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  // Encontra todas as etapas pelas quais o item passou
  const visitedStages = new Set();
  stageChanges.forEach(change => {
    if (change.details.new_stage) {
      visitedStages.add(change.details.new_stage);
    }
  });

  // Etapa atual
  const currentStage = props.item.funnel_stage;
  const currentStageIndex = stagesArray.findIndex(
    stage => stage.key === currentStage
  );

  // Determina etapas completadas baseado na ordem das posições
  const completedStages = [];
  if (currentStageIndex >= 0) {
    for (let i = 0; i < currentStageIndex; i++) {
      if (visitedStages.has(stagesArray[i].key)) {
        completedStages.push(stagesArray[i].key);
      }
    }
  }

  return {
    stages: stagesArray,
    currentStageIndex,
    completedStages,
    currentStage,
    visitedStages: Array.from(visitedStages),
  };
});

const agentInfo = computed(() => {
  // Primeiro tenta pegar assigned_agents
  const agent = props.item.assigned_agents?.[0] || props.item.agent;
  if (!agent) return null;

  return {
    name: agent.name,
    avatar_url: agent.avatar_url || agent.thumbnail || '',
  };
});

// Computed para determinar o status visual (ganho/perdido)
const itemStatus = computed(() => {
  const status = props.item.item_details?.status;
  const currentStage = props.item.funnel_stage;

  // Debug temporário
  console.log('DEBUG STATUS:', {
    status,
    currentStage,
    translation_won: t('KANBAN.STATUS.WON'),
    translation_lost: t('KANBAN.STATUS.LOST'),
    translation_completed: t('KANBAN.STATUS.COMPLETED'),
    translation_in_progress: t('KANBAN.STATUS.IN_PROGRESS'),
  });

  // Se tem status explícito, usa ele
  if (status === 'won') {
    return {
      type: 'won',
      label: t('KANBAN.STATUS.WON') || 'Ganho',
      class:
        'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    };
  }
  if (status === 'lost') {
    return {
      type: 'lost',
      label: t('KANBAN.STATUS.LOST') || 'Perdido',
      class: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    };
  }

  // Caso contrário, tenta inferir pelo estágio
  if (currentStage === 'resolved' || currentStage === 'closed') {
    return {
      type: 'won',
      label: t('KANBAN.STATUS.COMPLETED') || 'Concluído',
      class:
        'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    };
  }

  return {
    type: 'active',
    label: t('KANBAN.STATUS.IN_PROGRESS') || 'Em Andamento',
    class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  };
});
</script>

<template>
  <div class="header-section">
    <div class="flex items-center justify-between gap-4 mb-3">
      <h3 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
        {{ props.item.item_details.title }}
      </h3>
      <div class="flex items-center gap-2">
        <span
          v-if="
            props.item.item_details?.priority &&
            props.item.item_details.priority !== 'none'
          "
          class="px-3 py-1.5 text-sm font-medium rounded-full"
          :class="priorityClass"
        >
          {{
            priorityInfo?.label ||
            t(
              `KANBAN.PRIORITY_LABELS.${(
                props.item.item_details?.priority || 'NONE'
              ).toUpperCase()}`
            )
          }}
        </span>
        <span
          v-else
          class="px-3 py-1.5 text-sm font-medium rounded-full bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-50"
        >
          {{ t('KANBAN.PRIORITY_LABELS.NONE') }}
        </span>
      </div>
    </div>
    <p
      v-if="itemDescription"
      class="text-base text-slate-600 dark:text-slate-400 mb-4"
    >
      {{ itemDescription }}
    </p>

    <!-- Indicador de Progresso nas Etapas -->
    <div
      v-if="stageProgress.stages.length > 0"
      class="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700 mb-4"
    >
      <div class="flex items-center justify-between mb-3">
        <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
          {{ t('KANBAN.STAGE.PROGRESS') }}
        </span>
        <span
          class="text-xs text-slate-500 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded-full"
        >
          {{ stageProgress.currentStageIndex + 1 }} /
          {{ stageProgress.stages.length }}
        </span>
      </div>

      <div class="relative py-4">
        <!-- Etapas -->
        <div class="flex justify-between relative">
          <!-- Linha de progresso base -->
          <div
            class="absolute top-3 left-3 h-0.5 bg-slate-200 dark:bg-slate-700 rounded-full"
            style="width: calc(100% - 40px)"
          ></div>

          <!-- Linha de progresso ativa -->
          <div
            class="absolute top-3 left-3 h-0.5 bg-gradient-to-r from-woot-500 to-woot-600 rounded-full transition-all duration-700 ease-out"
            :style="{
              width:
                stageProgress.currentStageIndex > 0
                  ? `calc((100% - 40px) * ${
                      stageProgress.currentStageIndex /
                      Math.max(stageProgress.stages.length - 1, 1)
                    })`
                  : '0%',
            }"
          ></div>

          <div
            v-for="(stage, index) in stageProgress.stages"
            :key="stage.key"
            class="flex flex-col items-center group"
          >
            <!-- Círculo da etapa -->
            <div
              class="relative flex items-center justify-center w-6 h-6 rounded-full transition-all duration-300 group-hover:scale-110 z-10"
              :class="{
                // Etapa atual - azul vibrante
                'bg-woot-500 text-white shadow-lg shadow-woot-500/25':
                  index === stageProgress.currentStageIndex,
                // Etapas completadas - verde sutil
                'bg-green-500 text-white shadow-sm':
                  index < stageProgress.currentStageIndex &&
                  stageProgress.completedStages.includes(stage.key),
                // Etapas visitadas mas não completadas - amarelo suave
                'bg-yellow-400 text-white shadow-sm':
                  stageProgress.visitedStages.includes(stage.key) &&
                  index < stageProgress.currentStageIndex &&
                  !stageProgress.completedStages.includes(stage.key),
                // Etapas anteriores não visitadas - cinza igual às futuras
                'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400':
                  (index < stageProgress.currentStageIndex &&
                    !stageProgress.visitedStages.includes(stage.key)) ||
                  index > stageProgress.currentStageIndex,
              }"
            >
              <!-- Símbolo baseado no status -->
              <span
                v-if="
                  index < stageProgress.currentStageIndex &&
                  stageProgress.completedStages.includes(stage.key)
                "
                class="text-xs font-bold"
              >
                ✓
              </span>
              <span v-else class="text-xs font-medium">
                {{ index + 1 }}
              </span>
            </div>

            <!-- Nome da etapa -->
            <div class="mt-2 text-center max-w-16">
              <div
                class="text-xs font-medium transition-colors duration-200"
                :class="{
                  'text-woot-600 dark:text-woot-400':
                    index === stageProgress.currentStageIndex,
                  'text-green-600 dark:text-green-400':
                    index < stageProgress.currentStageIndex &&
                    stageProgress.completedStages.includes(stage.key),
                  'text-yellow-600 dark:text-yellow-400':
                    stageProgress.visitedStages.includes(stage.key) &&
                    index < stageProgress.currentStageIndex &&
                    !stageProgress.completedStages.includes(stage.key),
                  'text-slate-500 dark:text-slate-400':
                    (index < stageProgress.currentStageIndex &&
                      !stageProgress.visitedStages.includes(stage.key)) ||
                    index > stageProgress.currentStageIndex,
                }"
              >
                {{ stage.name }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Grid de informações principais -->
    <div class="info-grid">
      <!-- Agente Responsável -->
      <div class="info-card">
        <span class="info-label">
          {{ t('KANBAN.ITEM_DETAILS.AGENT_RESPONSIBLE') }}
        </span>
        <div class="info-content">
          <div v-if="loadingAgent" class="loading-text">
            {{ t('KANBAN.ITEM_DETAILS.LOADING_AGENT') }}
          </div>
          <div v-else-if="agentInfo" class="flex items-center gap-2">
            <Avatar
              :name="agentInfo.name"
              :src="agentInfo.avatar_url"
              :size="28"
            />
            <span class="font-medium">{{ agentInfo.name }}</span>
          </div>
          <div v-else class="no-data-text">
            {{ t('KANBAN.ITEM_DETAILS.NO_AGENT') }}
          </div>
        </div>
      </div>

      <!-- Conversa Relacionada -->
      <div v-if="props.item.item_details?.conversation_id" class="info-card">
        <span class="info-label">
          {{ t('KANBAN.FORM.CONVERSATION.LABEL') }}
        </span>
        <div
          class="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 min-h-[1.5rem] cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          @click="
            onNavigateToConversation($event, props.conversationData?.display_id)
          "
          @contextmenu="onContextMenu($event, props.item.conversation_id)"
        >
          <div class="flex items-center gap-2 flex-1">
            <fluent-icon
              icon="chat"
              class="text-slate-500 dark:text-slate-400 flex-shrink-0"
              size="16"
            />
            <div
              v-if="conversationData"
              class="flex items-center justify-between w-full"
            >
              <div
                class="text-xs text-slate-700 dark:text-slate-300 truncate my-0"
              >
                #{{ conversationData.display_id }} - {{ truncatedSenderName }}
                <span
                  class="ml-2 text-xs px-2 py-0.5 rounded-full"
                  :class="{
                    'bg-green-100 text-green-700':
                      conversationData.status === 'open',
                    'bg-yellow-100 text-yellow-700':
                      conversationData.status === 'pending',
                    'bg-slate-100 text-slate-700':
                      conversationData.status === 'resolved',
                  }"
                >
                  {{ conversationData.status }}
                </span>
              </div>
              <span
                v-if="conversationData.unread_count > 0"
                class="flex items-center justify-center h-4 min-w-[1rem] px-1 text-[0.625rem] font-medium bg-ruby-500 text-white rounded-full flex-shrink-0"
              >
                {{
                  conversationData.unread_count > 9
                    ? '9+'
                    : conversationData.unread_count
                }}
              </span>
            </div>
            <div
              v-else-if="loadingConversation"
              class="text-xs text-slate-500 flex items-center"
            >
              {{ t('KANBAN.LOADING_CONVERSATION') }}
            </div>
            <div v-else class="text-xs text-slate-500 flex items-center">
              {{ t('KANBAN.CONVERSATION_NOT_FOUND') }}
            </div>
          </div>
        </div>
      </div>

      <!-- Valor -->
      <div v-if="formattedValue" class="info-card">
        <span class="info-label">
          {{ t('KANBAN.FORM.VALUE.LABEL') }}
        </span>
        <span class="info-content value-text">
          {{ formattedValue }}
        </span>
      </div>

      <!-- Estágio Atual -->
      <div class="info-card">
        <span class="info-label">
          {{ t('KANBAN.FORM.STAGE.LABEL') }}
        </span>
        <div class="info-content flex items-center gap-2">
          <span class="font-medium">
            {{ currentStageName }}
          </span>
          <span class="text-xs text-slate-500">
            {{ formatStageDate(item) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.header-section {
  @apply bg-white dark:bg-slate-800
    rounded-lg
    p-4 mb-4
    border border-slate-100 dark:border-slate-700;
}

.info-grid {
  @apply grid gap-3 mt-3;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
}

.info-card {
  @apply relative overflow-hidden flex flex-col gap-1 p-3 rounded-lg transition-all duration-200
    bg-white dark:bg-slate-800 border dark:border-slate-700;
}

.info-card:nth-child(1) {
  @apply bg-n-iris-3 dark:bg-n-slate-8 border-n-iris-4 dark:border-n-iris-8/50;
}

.info-card:nth-child(2) {
  @apply bg-n-teal-3 dark:bg-n-slate-8 border-n-teal-4 dark:border-n-teal-8/50;
}

.info-card:nth-child(3) {
  @apply bg-n-amber-3 dark:bg-n-slate-8 border-n-amber-4 dark:border-n-amber-8/50;
}

.info-card:nth-child(4) {
  @apply bg-n-iris-3 dark:bg-n-slate-8 border-n-iris-4 dark:border-n-iris-8/50;
}

.info-label {
  @apply text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400;
}

.info-content {
  @apply text-sm font-medium text-slate-900 dark:text-slate-100;
}

.info-card:hover {
  @apply transform -translate-y-0.5 shadow-sm dark:shadow-slate-900/50;
}

.loading-text,
.no-data-text {
  @apply text-sm text-slate-500;
}

.value-text {
  @apply font-semibold text-n-teal-9 dark:text-n-teal-4;
}

.stage-progress-container {
  @apply bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700;
}

.stage-progress-container .relative {
  @apply py-2;
}

/* Responsividade para progresso em telas menores */
@media (max-width: 640px) {
  .stage-progress-container .flex.justify-between {
    @apply gap-1;
  }

  .stage-progress-container .text-xs {
    @apply text-[10px];
  }

  .stage-progress-container .w-8.h-8 {
    @apply w-6 h-6;
  }
}
</style> 